import React, { useEffect, useState } from "react";
import { AuthenticatedTemplate, UnauthenticatedTemplate, useMsal, useIsAuthenticated, useAccount } from "@azure/msal-react";
import Box from '@mui/material/Box';
import { EventType, EventMessage } from "@azure/msal-browser";
import { Outlet, useNavigate } from "react-router-dom";
import Paper from "@mui/material/Paper";
import LeftNav from "./ui/LeftNav";
import { msalConfig, b2cPolicies } from "../../utils/auth/authConfig";

import AppOwnsDataWebApi from 'services/AppOwnsDataWebApi';
import { PowerBiReport, PowerBiDataset } from 'models/models';
import { AppContext } from "AppContext";
const AppOwnsDataPage = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts  } = useMsal();
  const account = useAccount(accounts[0] || {});
  const [userLoginProcessed, setUserLoginProcessed ] = useState<boolean>(false)

  const [tenantName, setTenantName] = useState<string>("");
  const [reports, setReports] = useState<PowerBiReport[] | null>(null);
  const [datasets, setDatasets] = useState<PowerBiDataset[] | null>(null);
  const [user, setUser] = useState<string>("");
  const [userCanEdit, setUserCanEdit] = useState<boolean>(false);
  const [userCanCreate, setUserCanCreate] = useState<boolean>(false);
  const [workspaceArtifactsLoading, setWorkspaceArtifactsLoading] = useState<boolean>(false);
  const navigate = useNavigate();

  
  const selectAccount = async () => {

    const currentAccounts = instance.getAllAccounts();

    // if the are no accounts in cache, do nothing
    if (currentAccounts.length < 1) {
      return;
    }
    // if there is 1 account, set that account as the active account
    if (currentAccounts.length === 1) {
      instance.setActiveAccount(currentAccounts[0]);
      if (!userLoginProcessed) {
        await AppOwnsDataWebApi.LoginUser();
        setUserLoginProcessed(true);
      }
      return;
    }

    // if there are multiple accounts, ensure the account with the signup/signin policy is active
    if (currentAccounts.length > 1) {
      let currentAccountPolicy = (instance.getActiveAccount()?.idTokenClaims as any)?.tfp.toUpperCase();
      let requiredAccountPolicy = b2cPolicies?.names?.signUpSignIn?.toUpperCase();
      if (currentAccountPolicy !== requiredAccountPolicy) {
        console.log("WRONG ACCOUNT ACTIVE! - setting active account back to " + b2cPolicies?.names?.signUpSignIn);
        const filteredAccounts = currentAccounts.filter(account =>
          account.homeAccountId.toUpperCase().includes(b2cPolicies?.names?.signUpSignIn?.toUpperCase() ?? "") &&
          account?.idTokenClaims?.iss?.toUpperCase().includes(b2cPolicies?.authorityDomain?.toUpperCase() ?? "") &&
          account?.idTokenClaims?.aud === msalConfig.auth.clientId
        );

        // determine if search found signup/signin policy account
        if (filteredAccounts.length > 0) {
          // if so, set signup/signin policy accountas active account
          console.log("Setting SignUp/Signin policy as active account", filteredAccounts[0])
          instance.setActiveAccount(filteredAccounts[0]);
        }
      }
      if (!userLoginProcessed) {
        await AppOwnsDataWebApi.LoginUser();
        setUserLoginProcessed(true);
      }
    }
  }

  const handleMsalEventCallback = async (event: EventMessage) => {
    switch (event.eventType) {
      case EventType.INITIALIZE_END:
      case EventType.LOGIN_SUCCESS:
      case EventType.ACQUIRE_TOKEN_SUCCESS:
        selectAccount();
        break;
    }
  };

  const refreshEmbeddingData = () => {
    const refreshEmbeddingDataAsync = async () => {
      let viewModel = await AppOwnsDataWebApi.GetEmbeddingData();
      setReports(viewModel.reports);
    };
    refreshEmbeddingDataAsync();
  };

  // this code executes to initialize MSAL with callback function on every route change
  useEffect(() => {
    const callbackId = instance.addEventCallback(handleMsalEventCallback);
    
    // Call the function immediately to handle the current state
    handleMsalEventCallback({ eventType: EventType.INITIALIZE_END } as EventMessage);

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]); // Added instance as a dependency to ensure it runs on route change

  useEffect(() => {
    const getEmbeddingDataAsync = async () => {
      setWorkspaceArtifactsLoading(true);
      let viewModel = await AppOwnsDataWebApi.GetEmbeddingData();
      setTenantName(viewModel.tenantName);
      setReports(viewModel.reports);
      setDatasets(viewModel.datasets);
      setUser(account?.name ?? "unknown_user");
      setUserCanEdit(viewModel.userCanEdit);
      setUserCanCreate(viewModel.userCanCreate);
      setWorkspaceArtifactsLoading(false);
    }
    if (isAuthenticated && userLoginProcessed) {
      getEmbeddingDataAsync()
    } else if (localStorage.getItem('loginRedirectedFrom') !== "") {
      localStorage.setItem('loginRedirectedFrom', "reports");
      navigate('/');
    }

  }, [isAuthenticated, userLoginProcessed]);

  return (
    <AppContext.Provider value={{
      embeddingData: {
        tenantName: tenantName,
        reports: reports || [],
        datasets: datasets || [],
        user: user,
        userCanEdit: userCanEdit,
        userCanCreate: userCanCreate,
        workspaceArtifactsLoading: workspaceArtifactsLoading
      },
      refreshEmbeddingData: refreshEmbeddingData,
    }}>
      <AuthenticatedTemplate>
        {!navigator.onLine && (
          <Paper
            elevation={1}
            style={{
              backgroundColor: "#F0F0F0",
              padding: "10px",
              textAlign: "center",
              position: "relative",
              zIndex: "100",
            }}
          >
            <span
              style={{
                fontFamily: "Inter",
                fontWeight: "500",
                color: "#556670",
                fontSize: "14px",
              }}
            >
              Offline Mode:
            </span>
            <span
              style={{
                fontFamily: "Jost",
                fontWeight: "normal",
                color: "#556670",
                fontSize: "14px",
              }}
            >
              {" "}
              Your test results will save locally. Other features are limited.
            </span>
          </Paper>
        )}
        <Box>
          <Box sx={{ display: "flex" }} >
            <LeftNav />
            <Outlet />
          </Box>
        </Box>
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        {/* Content for unauthenticated users */}
      </UnauthenticatedTemplate>
    </AppContext.Provider>
  );
};

export default AppOwnsDataPage;
